import { useEffect, useMemo, useRef } from "react";

/**
 * Stable debounced wrapper around the latest ``fn``. Pending calls are
 * flushed (not dropped) on unmount so a draft autosave can't lose the
 * final keystrokes when the user navigates away mid-debounce.
 */
export function useDebouncedCallback<Args extends unknown[]>(
  fn: (...args: Args) => void,
  delayMs: number,
): (...args: Args) => void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const state = useMemo(
    () => ({
      timer: null as ReturnType<typeof setTimeout> | null,
      pendingArgs: null as Args | null,
    }),
    [],
  );

  useEffect(() => {
    return () => {
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
        if (state.pendingArgs) fnRef.current(...state.pendingArgs);
        state.pendingArgs = null;
      }
    };
  }, [state]);

  return useMemo(
    () =>
      (...args: Args) => {
        state.pendingArgs = args;
        if (state.timer) clearTimeout(state.timer);
        state.timer = setTimeout(() => {
          state.timer = null;
          state.pendingArgs = null;
          fnRef.current(...args);
        }, delayMs);
      },
    [delayMs, state],
  );
}
